import React from 'react';
import { Loader } from 'AppSrc/core/components/utility/loader';
import { connect } from 'react-redux';
import { CREATE_CRQ } from 'AppSrc/core/components/create-crq-screen/create-crq-screen-consts';
import PropTypes from 'prop-types';
import { WORK_INFORMATION_ITEMS } from 'AppSrc/core/components/forms/form-types/create-crq/section-five/section-five';
import {
  ATTACHED_FILE,
  WORK_INFORMATION_ITEMS_FIELD_ID,
} from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-constants';
import { getFormDataFieldValue, getFieldValueForParameters } from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import { WarningTriangleFilled } from 'AppSrc/core/components/common/svg-icons';
import { Alert } from 'AppSrc/core/components/utility/alert';
import { clearSubmitFormError } from 'AppSrc/core/actions/actions-main-form';
import { clearSubmitEditFormError } from 'AppSrc/core/actions/actions-edit-form';

const SubmitCreateCrqProgressDisplay = ({
  isSubmitting,
  isUpdating,
  progressArray,
  hasSubmitError,
  errorAlertData,
  resolveAlert,
  message,
}) => {
  const submitType = isUpdating ? 'update' : 'create';

  let userDisplayError = 'Sorry we are unable to create your CRQ due to a technical issue. Please try again later.';
  if (message === 'TIL Error: 500 E Approver ID/Sponsor ID not found in Remedy') {
    userDisplayError = 'Sorry, We are unable to process your CRQ at this moment as the Sponsor Id entered is not found in Remedy. Please try again later making sure you have entered a valid Sponsor Id.';
  }
  if (message === 'TIL Error: 104 E ; The operational categorization information is invalid for the specified company, "Vodafone Limited".  Use the menus to select this information.') {
    userDisplayError = 'Sorry, we are unable to create your CRQ as the Operational Categorization information selected is invalid. Please try again later by selecting the valid options from dropdowns.';
  }
  if (message === 'Remedy Error Message -  The infrastructure change location information is invalid. Use the menus on the Region, Site Group, and Site fields or the type ahead return function on the Site field to select this information.') {
    userDisplayError = 'Sorry, we are unable to create your CRQ as the information related to Change location seems to be invalid. Please check and try again later by reselecting from the dropdowns on the Region, Site Group and Site field.';
  }
  if (message === 'Error : Remedy Error Message - The selected Change Coordinator is invalid. Reselect the Change Coordinator using the menu, then reapply.') {
    userDisplayError = 'Sorry, We are unable to update your CRQ as the selected Change Coordinator is invalid. Please try again later by reselecting the change coordinator using the menu.';
  }
  if (message === 'Remedy Error Message -  This Change Request has at least one open task or task group. Close all children tasks before setting Change Status to Completed or Closed. \nMessageNumber - 45075') {
    userDisplayError = 'Sorry, We are unable to process your CRQ as ACE does not support Task Management at this moment. Please close the open tasks against this Change in Remedy. Any inconvenience caused is regretted!';
  }

  // else {
  //   userDisplayError = 'Sorry we are unable to create your CRQ due to a technical issue. Please try again later.';
  // }

  if (message !== null) {
    errorAlertData = {
      ...errorAlertData,
      alertTitle: userDisplayError,
    };
  }

  if (hasSubmitError) {
    return (
      <Alert
        alertData={errorAlertData}
        resolveAlert={resolveAlert}
      />
    );
  }
  if (!isSubmitting) { return null; }
  return (
    <Loader
      loaderArray={progressArray}
      loaderTitle={`Please wait whilst we ${submitType} your CRQ`}
      loaderText="Do not close or refresh your browser whilst we do this."
    />
  );
};

SubmitCreateCrqProgressDisplay.propTypes = {
  isSubmitting: PropTypes.bool,
  isUpdating: PropTypes.bool,
  progressArray: PropTypes.arrayOf(PropTypes.bool),
  hasSubmitError: PropTypes.bool,
  resolveAlert: PropTypes.func,
  message: PropTypes.string,
  errorAlertData: PropTypes.shape({
    className: PropTypes.string,
    trueText: PropTypes.string,
    showFalse: PropTypes.bool,
    alertTitle: PropTypes.string,
    Icon: PropTypes.elementType,
  }),
};

SubmitCreateCrqProgressDisplay.defaultProps = {
  errorAlertData: {
    className: 'alert-error',
    trueText: 'Close',
    showFalse: false,
    alertTitle: 'Sorry we are unable to create your CRQ due to a technical issue. Please try again later.',
    Icon: WarningTriangleFilled,
  },
};



// method to create a progress array of booleans for the laoder
// based on the state crq and work info item data
const generateProgressArray = (params) => {
  const {
    state,
    editCrqId,
  } = params;

  const crqData = editCrqId ? state.editForm.formDataById[editCrqId] : state.mainForm.formData[CREATE_CRQ];
  const { crqId } = crqData;
  const workInfoItemData = state.workInfoItem;
  const { attachmentsRead, workInfoItemsByCrqId } = workInfoItemData;
  // booleans for the state of the submit progress
  // need to stay true once the milestone has been passed
  const crqSaved = Boolean(crqId);

  const isSubmitting = crqData.isSubmitting;
  const startedSavingCrq = isSubmitting || crqSaved;
  const workInfoItemsSaved = Boolean(workInfoItemsByCrqId[crqId]);
  const isSavingWiis = workInfoItemData.isSaving;
  const startedSavingWiis = isSavingWiis || workInfoItemsSaved;

  // get a list of work info item file ids in form state
  const modalData = getFormDataFieldValue({
    formData: crqData,
    sectionId: WORK_INFORMATION_ITEMS,
    fieldId: WORK_INFORMATION_ITEMS_FIELD_ID,
  });
  const sectionIds = modalData ? modalData.formSectionsByDisplayOrder : [];
  const fileIds = sectionIds.map((sectionId) => {
    const fileId = getFieldValueForParameters({
      formData: modalData,
      sectionId,
      fieldId: ATTACHED_FILE,
    });
    return fileId;
  });

  // Create an array of booleans based on whether the file is read
  // File has been read if the attachmentsRead array includes the file id
  // or if the work info item has been saved
  const filesRead = fileIds.map((id) => workInfoItemsSaved || attachmentsRead.includes(id));

  // generate the progress array based on the state booleans and the files read
  return ([
    startedSavingCrq,
    crqSaved,
    ...filesRead,
    startedSavingWiis,
    workInfoItemsSaved,
  ]);
};

const mapStateToProps = (state, ownProps) => {
  const {
    editCrqId,
  } = ownProps;
  const crqData = editCrqId ? state.editForm.formDataById[editCrqId] : state.mainForm.formData[CREATE_CRQ];
  const { isSubmitting, submitError } = crqData;

  const { message } = submitError || {};
  const progressArray = isSubmitting ? generateProgressArray({
    state,
    editCrqId,
  }) : null;
  const hasSubmitError = Boolean(submitError);
  // If the crq has an id, then it's updating an existing crq
  const isUpdating = crqData.crqId ? true : false;
  return ({
    hasSubmitError,
    isSubmitting,
    progressArray,
    isUpdating,
    message,
  });
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  resolveAlert(boolean) {
    const {
      editCrqId,
    } = ownProps;
    // If boolean true then right button will refresh
    if (editCrqId && boolean.boolean === true) {
      // right button click for edit crq error should refresh the page.
      window.location.reload();
      return;
    }
    // If boolean false then left button will refresh
    if (editCrqId && boolean.boolean === false) {
      // left hand button click for edit crq should clear the error
      dispatch(clearSubmitEditFormError(editCrqId));
      return;
    }
    // We are creating a crq. The alert has a single close button.
    // Resolving alert should clear the create crq form error
    dispatch(clearSubmitFormError(CREATE_CRQ));
  },
});

const SubmitCreateCrqProgress = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SubmitCreateCrqProgressDisplay);

export { SubmitCreateCrqProgress, SubmitCreateCrqProgressDisplay };
